/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { property } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DMarkdown from '../markdown/markdown';
import './content-group-paragraph';
import styles from './content-group.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * The copy content in content group.
 *
 * @element c4d-content-group-copy
 * @csspart paragraph - The paragraph element within the content group copy. Usage: `c4d-content-group-copy::part(paragraph)`
 */
@customElement(`${c4dPrefix}-content-group-copy`)
class C4DContentGroupCopy extends StableSelectorMixin(C4DMarkdown) {
  protected get _customTags() {
    const tags = new Set(super._customTags);
    tags.add(`${c4dPrefix}-content-group-paragraph`);
    return tags;
  }

  protected get _renderer() {
    return Object.assign(super._renderer, {
      paragraph(text) {
        return `<${c4dPrefix}-content-group-paragraph part="paragraph">${text}</${c4dPrefix}-content-group-paragraph>`;
      },
    });
  }

  firstUpdated() {
    this.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        this.removeChild(node);
      }
    });
  }

  /**
   * The shadow slot this copy content should be in.
   */
  @property({ reflect: true })
  slot = 'copy';

  static get stableSelector() {
    return `${c4dPrefix}--content-group__copy`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DContentGroupCopy;
